import { useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconNavigationChevronRight } from "@/assets/icons/figma/IconNavigationChevronRight";
import { IconStateCheckmark } from "@/assets/icons/figma/IconStateCheckmark";
import { IconStateClockTime } from "@/assets/icons/figma/IconStateClockTime";
import {
  ReservationProjectCreateDialog,
  ReservationProjectField,
} from "@/features/pro-buyer-projects/components/ReservationProjectField";
import { ReservationRequestSendButton } from "@/features/pro-buyer-projects/components/ReservationRequestSendButton";
import { ReservationDialog } from "@/features/pro-buyer-reservations/components/ReservationDialog";
import { ReservationMoney } from "@/features/pro-buyer-reservations/data/reservations-client";
import { PdpReservationView } from "@/features/pro-buyer-reservations/hooks/usePdpReservation";
import { useReservationActions } from "@/features/pro-buyer-reservations/hooks/useReservations";
import {
  formatReservationDate,
  formatReservationDateTime,
  formatReservationMoney,
} from "@/features/pro-buyer-reservations/utils/reservation-format";
import addToast from "@/toast/addToast";
import { Button } from "@/ui/cva/Button";

const REQUEST_WINDOW_MS = 48 * 60 * 60 * 1000;

type PdpReservationBlockProps = {
  productId: string;
  view: PdpReservationView;
  /** The price the reservation would fix: the Pro Buyer price, else the regular one. */
  price: ReservationMoney | null;
  /** True when `price` is the viewer's Pro Buyer price. */
  isProBuyerPrice: boolean;
};

const BLOCKED_COPY = {
  LIMIT_REACHED: {
    key: "pdp:reservation-blocked-limit",
    fallback:
      "You have 3 open reservations or requests. Release one to reserve this piece.",
  },
  COOLDOWN: {
    key: "pdp:reservation-blocked-cooldown",
    // F31: also shown after the seller declined, so it does not say "ended" only.
    fallback:
      "The seller did not accept your request, or your reservation of this piece has ended. You can request it again 7 days later.",
  },
  ALREADY_REQUESTED: {
    key: "pdp:reservation-blocked-already-requested",
    fallback: "You already sent a request for this piece.",
  },
} as const;

/**
 * WHO-5947, spec 1a to 1c. The reservation part of the product page, under
 * Add to cart / Make an offer. Never names the seller: always "the seller".
 */
export const PdpReservationBlock = ({
  productId,
  view,
  price,
  isProBuyerPrice,
}: PdpReservationBlockProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const actions = useReservationActions();
  const [dialog, setDialog] = useState<
    "request" | "release" | "withdraw" | null
  >(null);
  const [busy, setBusy] = useState(false);
  // WHO-5959 6e: the optional project of the request, and the inline "Nieuw project" dialog.
  const [projectId, setProjectId] = useState<string | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);

  if (view.mode === "none") {
    return null;
  }

  const formattedPrice = formatReservationMoney(price, locale);

  const fail = () =>
    addToast(
      t(
        "pdp:reservation-error",
        "That did not work. Refresh the page and try again."
      ),
      { appearance: "error" }
    );

  const run = async (action: () => Promise<{ ok: boolean }>) => {
    setBusy(true);

    try {
      const result = await action();

      if (!result.ok) {
        fail();
      }

      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  if (view.mode === "others") {
    return (
      <p
        className="font-body text-sm text-grey-800"
        data-testid="pdp-reservation-others"
      >
        {t("pdp:reservation-others-explanation", {
          defaultValue:
            "This piece is reserved for another buyer until {{date}}. After that it is for sale again. Add it to your favorites to find it back.",
          date: formatReservationDate(view.reservedUntilDate, locale),
        })}
      </p>
    );
  }

  if (view.mode === "mine") {
    const until = formatReservationDateTime(
      view.reservation.reservedUntil,
      locale
    );
    const fixed = formatReservationMoney(view.reservation.fixedPrice, locale);

    return (
      <div
        className="flex flex-col gap-3 rounded-[8px] bg-curious-100 p-4"
        data-testid="pdp-reservation-mine"
      >
        <p className="font-title font-bold text-curious-800">
          {t("pdp:reservation-mine-title", {
            defaultValue: "Reserved for you until {{date}}",
            date: until,
          })}
        </p>
        {fixed && (
          <p className="font-body text-sm text-grey-800">
            {t("pdp:reservation-mine-price", {
              defaultValue:
                "Your price of {{price}} is fixed until then. Buy before then and the piece is yours.",
              price: fixed,
            })}
          </p>
        )}
        <Button
          type="button"
          appearance="minimal"
          size="small"
          className="min-h-11 self-start px-0"
          onClick={() => setDialog("release")}
        >
          {t("pdp:reservation-release", "Release reservation")}
        </Button>
        <ReservationDialog
          open={dialog === "release"}
          onOpenChange={open => setDialog(open ? "release" : null)}
          title={t(
            "pdp:reservation-release-title",
            "Release this reservation?"
          )}
          actions={
            <>
              <Button
                type="button"
                appearance="outlined"
                className="w-full sm:w-auto"
                onClick={() => setDialog(null)}
              >
                {t("pdp:reservation-cancel", "Cancel")}
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                loading={busy}
                onClick={() => run(() => actions.release(view.reservation.id))}
              >
                {t("pdp:reservation-release-confirm", "Release")}
              </Button>
            </>
          }
        >
          <p>
            {t(
              "pdp:reservation-release-body-lapse",
              "The piece is for sale to everyone again straight away and your fixed price lapses. You can request this piece again only after 7 days."
            )}
          </p>
        </ReservationDialog>
      </div>
    );
  }

  if (view.mode === "requested") {
    return (
      <div
        className="flex flex-col gap-2 rounded-[8px] bg-burst-100 p-4"
        data-testid="pdp-reservation-requested"
      >
        <p className="font-title font-bold text-burst-900">
          {t(
            "pdp:reservation-requested-title",
            "Request sent, waiting for the seller"
          )}
        </p>
        <p className="font-body text-sm text-grey-800">
          {t("pdp:reservation-requested-body", {
            defaultValue: "The seller can answer until {{date}}.",
            date: formatReservationDateTime(
              view.reservation.requestExpiresAt,
              locale
            ),
          })}
        </p>
        <Button
          type="button"
          appearance="minimal"
          size="small"
          className="min-h-11 self-start px-0"
          onClick={() => setDialog("withdraw")}
        >
          {t("pdp:reservation-withdraw", "Withdraw request")}
        </Button>
        <ReservationDialog
          open={dialog === "withdraw"}
          onOpenChange={open => setDialog(open ? "withdraw" : null)}
          title={t("pdp:reservation-withdraw-title", "Withdraw this request?")}
          actions={
            <>
              <Button
                type="button"
                appearance="outlined"
                className="w-full sm:w-auto"
                onClick={() => setDialog(null)}
              >
                {t("pdp:reservation-cancel", "Cancel")}
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                loading={busy}
                onClick={() => run(() => actions.release(view.reservation.id))}
              >
                {t("pdp:reservation-withdraw-confirm", "Withdraw")}
              </Button>
            </>
          }
        >
          <p>
            {t(
              "pdp:reservation-withdraw-body",
              "The seller no longer sees your request. You can request this piece again only after 7 days."
            )}
          </p>
        </ReservationDialog>
      </div>
    );
  }

  const answerBy =
    view.eligibility.answerBy ??
    new Date(Date.now() + REQUEST_WINDOW_MS).toISOString();
  const blocked = view.blockedReason ? BLOCKED_COPY[view.blockedReason] : null;

  return (
    <div className="flex flex-col gap-1" data-testid="pdp-reservation-request">
      {/* F27: an outlined card under "Plaats bod", the whole card is the button. */}
      <button
        type="button"
        className="flex min-h-16 w-full items-center gap-3 rounded border border-grey-300 bg-white px-3 py-3 text-left transition-colors hover:bg-grey-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed disabled:opacity-50 md:px-4"
        disabled={!!blocked}
        onClick={() => setDialog("request")}
        data-testid="pdp-reservation-card"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded bg-flavour-100 text-brand-500">
          <IconStateClockTime className="size-6" aria-hidden="true" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-title text-base font-bold text-black">
            {t(
              "pdp:reservation-card-title",
              "Reserve for 48 hours for a client"
            )}
          </span>
          <span className="font-body text-sm text-grey-700">
            {t(
              "pdp:reservation-card-help",
              "Ask for it, the seller decides within 48 hours."
            )}
          </span>
        </span>
        <IconNavigationChevronRight
          className="size-6 shrink-0 text-grey-700"
          aria-hidden="true"
        />
      </button>
      {blocked && (
        <p className="font-body text-sm text-grey-700">
          {t(blocked.key, blocked.fallback)}
        </p>
      )}
      <ReservationDialog
        open={dialog === "request" && !creatingProject}
        onOpenChange={open => setDialog(open ? "request" : null)}
        // F35: same title as the card (F28).
        title={t("pdp:reservation-request-title", "Reserve for 48 hours")}
        actions={
          <>
            <Button
              type="button"
              appearance="outlined"
              className="w-full sm:w-auto"
              onClick={() => setDialog(null)}
            >
              {t("pdp:reservation-cancel", "Cancel")}
            </Button>
            <ReservationRequestSendButton
              productId={productId}
              projectId={projectId}
              loading={busy}
              run={send => void run(send)}
              requestWithoutProject={() => actions.request(productId)}
            >
              {t("pdp:reservation-send-request", "Send request")}
            </ReservationRequestSendButton>
          </>
        }
      >
        {/* F35: Evelien's short version (F28 addition): intro, 4 checks, help line. */}
        <p>
          {t(
            "pdp:reservation-request-intro",
            "Ask the seller to hold this piece for you for 48 hours."
          )}
        </p>
        <ul className="flex flex-col gap-2" data-testid="reservation-points">
          {[
            t("pdp:reservation-request-point-nobody", "Nobody else can buy it"),
            isProBuyerPrice
              ? t("pdp:reservation-request-point-pro-price", {
                  defaultValue: "Your Pro Buyer price of {{price}} is fixed",
                  price: formattedPrice,
                })
              : t("pdp:reservation-request-point-price", {
                  defaultValue: "The price of {{price}} is fixed",
                  price: formattedPrice,
                }),
            t(
              "pdp:reservation-request-point-free",
              "Free and without obligation, even if your client says no"
            ),
            t(
              "pdp:reservation-request-point-anonymous",
              "The seller does not see who you are"
            ),
          ].map(point => (
            <li key={point} className="flex items-start gap-2">
              <IconStateCheckmark
                className="size-5 shrink-0 text-brand-500"
                aria-hidden="true"
              />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        <p className="font-body text-sm text-grey-700">
          {t("pdp:reservation-request-help", {
            defaultValue:
              "The seller can answer until {{date}}. A question about the piece? Use the chat.",
            date: formatReservationDateTime(answerBy, locale),
          })}
        </p>
        {dialog === "request" && (
          <ReservationProjectField
            value={projectId}
            onChange={setProjectId}
            onRequestCreate={() => setCreatingProject(true)}
          />
        )}
      </ReservationDialog>
      {dialog === "request" && (
        <ReservationProjectCreateDialog
          open={creatingProject}
          onOpenChange={setCreatingProject}
          onCreated={setProjectId}
        />
      )}
    </div>
  );
};
